import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { DatePicker } from '@/components/ui/date-picker';
import { Trash2, Plus, GripVertical } from 'lucide-react';
import { useUIConfig } from '@/config/ui-config';

export interface MatterPhaseEntry {
  id?: number;
  name: string;
  description: string;
  budget: number;
  order: number;
  start_date?: string | null;
  end_date?: string | null;
}

interface MatterPhasesFormProps {
  phases: MatterPhaseEntry[];
  onChange: (phases: MatterPhaseEntry[]) => void;
  firmId?: string;
  className?: string;
}

const currencyFormatter = new Intl.NumberFormat('en-BE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function MatterPhasesForm({
  phases,
  onChange,
  firmId,
  className = '',
}: MatterPhasesFormProps) {
  const { form } = useUIConfig(firmId);

  const addPhase = () => {
    onChange([
      ...phases,
      {
        name: `Phase ${phases.length + 1}`,
        description: '',
        budget: 0,
        order: phases.length,
        start_date: null,
        end_date: null,
      },
    ]);
  };

  const updatePhase = (index: number, field: keyof MatterPhaseEntry, value: string | number | null) => {
    const newPhases = [...phases];
    newPhases[index] = { ...newPhases[index], [field]: value };
    onChange(newPhases);
  };

  const removePhase = (index: number) => {
    const newPhases = phases
      .filter((_, idx) => idx !== index)
      .map((p, idx) => ({ ...p, order: idx }));
    onChange(newPhases);
  };

  const movePhase = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= phases.length) return;
    const newPhases = [...phases];
    const [moved] = newPhases.splice(fromIndex, 1);
    newPhases.splice(toIndex, 0, moved);
    onChange(newPhases.map((p, idx) => ({ ...p, order: idx })));
  };

  const totalPhaseBudget = phases.reduce((sum, p) => sum + (Number(p.budget) || 0), 0);

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <Label className={`text-base ${form.labelFontWeight}`}>
            Phases
          </Label>
          <p className="text-sm text-muted-foreground mt-0.5">
            Break down the matter into phases, each with its own budget.
          </p>
        </div>
        {phases.length > 0 && (
          <div className="text-sm text-right">
            <span className="text-muted-foreground">Total phase budget: </span>
            <span className="font-semibold">{currencyFormatter.format(totalPhaseBudget)}</span>
          </div>
        )}
      </div>

      {phases.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Phase Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[130px]">Start Date</TableHead>
              <TableHead className="w-[130px]">End Date</TableHead>
              <TableHead className="w-[140px]">Budget</TableHead>
              <TableHead className="w-[50px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {phases.map((phase, index) => (
              <TableRow key={phase.id ?? `new-${index}`}>
                <TableCell className="px-1">
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      onClick={() => movePhase(index, index - 1)}
                      disabled={index === 0}
                      tabIndex={-1}
                    >
                      <svg width="12" height="8" viewBox="0 0 12 8"><path d="M6 0l6 8H0z" fill="currentColor"/></svg>
                    </button>
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50" />
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                      onClick={() => movePhase(index, index + 1)}
                      disabled={index === phases.length - 1}
                      tabIndex={-1}
                    >
                      <svg width="12" height="8" viewBox="0 0 12 8"><path d="M6 8l6-8H0z" fill="currentColor"/></svg>
                    </button>
                  </div>
                </TableCell>

                <TableCell>
                  <Input
                    value={phase.name}
                    onChange={(e) => updatePhase(index, 'name', e.target.value)}
                    placeholder="e.g. Phase 1 — Research"
                    className="bg-background"
                  />
                </TableCell>

                <TableCell>
                  <Input
                    value={phase.description}
                    onChange={(e) => updatePhase(index, 'description', e.target.value)}
                    placeholder="Optional description"
                    className="bg-background"
                  />
                </TableCell>

                <TableCell>
                  <DatePicker
                    value={phase.start_date}
                    onChange={(iso) => updatePhase(index, 'start_date', iso || null)}
                    placeholder="Start"
                    className="w-[130px] h-9 px-2 text-xs"
                  />
                </TableCell>

                <TableCell>
                  <DatePicker
                    value={phase.end_date}
                    onChange={(iso) => updatePhase(index, 'end_date', iso || null)}
                    placeholder="End"
                    className="w-[130px] h-9 px-2 text-xs"
                  />
                </TableCell>

                <TableCell>
                  <div className="flex items-center space-x-1">
                    <span className="text-sm text-muted-foreground">€</span>
                    <Input
                      type="number"
                      step="100"
                      min="0"
                      placeholder="0"
                      value={phase.budget || ''}
                      onChange={(e) => updatePhase(index, 'budget', Number(e.target.value) || 0)}
                      className="w-[110px] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      style={{ MozAppearance: 'textfield' as any }}
                    />
                  </div>
                </TableCell>

                <TableCell>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    onClick={() => removePhase(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Remove phase</span>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={addPhase}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Phase
      </Button>

      {phases.length === 0 && (
        <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
          No phases defined. The matter will use a single overall budget. Add phases to break the budget down into stages.
        </div>
      )}
    </div>
  );
}
